import { QueryClient, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { axiosInstance } from '@/api/axios'
import {
  CustomerTotalResponse,
  GETCustomerPeriodResponse,
  GETCustomerSegmentsResponse,
  PATCHCustomerPeriodRequest,
  PATCHCustomerSegmentRequest,
  PATCHCustomerSegmentResponse,
  POSTCustomerPeriodRequest,
  POSTCustomerPeriodResponse,
  POSTCustomerSegmentRequest,
  POSTCustomerSegmentResponse,
  SwitchCustomerSegmentPeriodInputRequest
} from './types'
import { createQueryKey, FINANCIAL_MODEL_SETTINGS_BASE_KEY, resetGlobalQueryKey, REVENUES_BASE_KEY, TOTALS_KEY } from '@/api/query-keys'
import { PeriodType, PeriodTypeQuery, PeriodTypeView } from '@/types'

export const CUSTOMER_SEGMENTS_KEY = createQueryKey({
  baseKey: REVENUES_BASE_KEY,
  keys: [`customer-segments`]
})

export const CUSTOMER_SEGMENTS_DATA_KEY = CUSTOMER_SEGMENTS_KEY.concat([`segment-data`])

export const getCustomerSegments = async (financialModelId: string) => {
  const response = await axiosInstance.get<GETCustomerSegmentsResponse>(`/financial-models/${financialModelId}/customer-segments?sort_by=created_at`)
  return response.data
}

export const useGetCustomerSegmentsQuery = (financialModelId: string) =>
  useSuspenseQuery({
    queryKey: CUSTOMER_SEGMENTS_KEY.concat([financialModelId]),
    queryFn: () => getCustomerSegments(financialModelId)
  })

export const postCustomerSegment = async (
  financialModelId: string,
  customerSegment: POSTCustomerSegmentRequest
): Promise<POSTCustomerSegmentResponse> => {
  const response = await axiosInstance.post<POSTCustomerSegmentResponse>(`/financial-models/${financialModelId}/customer-segments`, customerSegment)

  return response.data
}

export const useCreateCustomerSegmentMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (customerSegment: POSTCustomerSegmentRequest) => postCustomerSegment(financialModelId, customerSegment),
    mutationKey: [`new-customer-segment`],
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REVENUES_BASE_KEY })
      queryClient.invalidateQueries({
        queryKey: FINANCIAL_MODEL_SETTINGS_BASE_KEY
      })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const duplicateCustomerSegment = async (financialModelId: string, customerSegments: POSTCustomerSegmentRequest[]) => {
  const promises = customerSegments.map(customerSegment =>
    axiosInstance.post<POSTCustomerSegmentResponse>(`/financial-models/${financialModelId}/customer-segments/`, {
      ...customerSegment,
      name: `${customerSegment.name} - Copy`
    })
  )
  const responses = await Promise.all(promises)
  return responses.map(response => response.data)
}

export const useDuplicateCustomerSegmentMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (customerSegments: POSTCustomerSegmentRequest[]) => duplicateCustomerSegment(financialModelId, customerSegments),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REVENUES_BASE_KEY })
      queryClient.invalidateQueries({
        queryKey: FINANCIAL_MODEL_SETTINGS_BASE_KEY
      })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const patchCustomerSegment = async (
  financialModelId: string,
  id: string,
  customerSegment: PATCHCustomerSegmentRequest
): Promise<PATCHCustomerSegmentResponse> => {
  const response = await axiosInstance.patch<PATCHCustomerSegmentResponse>(
    `/financial-models/${financialModelId}/customer-segments/${id}`,
    customerSegment
  )

  return response.data
}

export const useUpdateCustomerSegmentMutation = (financialModelId: string, id: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (customerSegment: PATCHCustomerSegmentRequest) => patchCustomerSegment(financialModelId, id, customerSegment),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REVENUES_BASE_KEY })
      queryClient.invalidateQueries({
        queryKey: FINANCIAL_MODEL_SETTINGS_BASE_KEY
      })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const deleteCustomerSegment = async (financialModelId: string, ids: string[]) => {
  const promises = ids.map(id => axiosInstance.delete(`/financial-models/${financialModelId}/customer-segments/${id}`))
  const responses = await Promise.all(promises)
  return responses
}

export const useDeleteCustomerSegmentMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (ids: string[]) => deleteCustomerSegment(financialModelId, ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: REVENUES_BASE_KEY })
      queryClient.invalidateQueries({
        queryKey: FINANCIAL_MODEL_SETTINGS_BASE_KEY
      })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const getAllCustomerSegmentPeriodsData = async (financialModelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) => {
  const response = await axiosInstance.get<GETCustomerPeriodResponse>(`/financial-models/${financialModelId}/customer-segment-data/`, {
    params: {
      type: period,
      view: view ?? `monthly`
    }
  })
  return response.data
}

export const useGetAllCustomerSegmentPeriodsDataQuery = (financialModelId: string, period: PeriodTypeQuery, view?: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: CUSTOMER_SEGMENTS_DATA_KEY.concat([financialModelId, period, view ?? `monthly`]),
    queryFn: () => getAllCustomerSegmentPeriodsData(financialModelId, period, view)
  })

export const postCustomerSegmentPeriod = async (financialModelId: string, customerSegmentPeriod: POSTCustomerPeriodRequest) => {
  const response = await axiosInstance.post<POSTCustomerPeriodResponse>(
    `/financial-models/${financialModelId}/customer-segment-data/`,
    customerSegmentPeriod
  )
  return response.data
}

export const postCustomerSegmentPeriodBulk = async (financialModelId: string, customerSegmentPeriods: POSTCustomerPeriodRequest[]) => {
  const response = await axiosInstance.post(`/financial-models/${financialModelId}/customer-segment-data/bulk/`, {
    periods: customerSegmentPeriods
  })

  return response.data
}

export const getCreateCustomerSegmentPeriodMutationArgs = (financialModelId: string, queryClient: QueryClient) => ({
  mutationFn: (customerSegmentPeriod: POSTCustomerPeriodRequest) => postCustomerSegmentPeriod(financialModelId, customerSegmentPeriod),
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: CUSTOMER_SEGMENTS_DATA_KEY.concat([financialModelId])
    })
    resetGlobalQueryKey([TOTALS_KEY])
  }
})

export const useCreateCustomerSegmentPeriodMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation(getCreateCustomerSegmentPeriodMutationArgs(financialModelId, queryClient))
}

export const patchCustomerSegmentPeriod = async (financialModelId: string, id: string, customerSegmentPeriod: PATCHCustomerPeriodRequest) => {
  const response = await axiosInstance.patch<GETCustomerPeriodResponse>(
    `/financial-models/${financialModelId}/customer-segment-data/${id}`,
    customerSegmentPeriod
  )
  return response.data
}

export const getUpdateCustomerSegmentPeriodMutationArgs = (financialModelId: string, id: string, queryClient: QueryClient) => ({
  mutationFn: (customerSegmentPeriod: PATCHCustomerPeriodRequest) => patchCustomerSegmentPeriod(financialModelId, id, customerSegmentPeriod),
  onSuccess: () => {
    queryClient.invalidateQueries({
      queryKey: CUSTOMER_SEGMENTS_DATA_KEY.concat([financialModelId])
    })
    resetGlobalQueryKey([TOTALS_KEY])
  }
})

export const useUpdateCustomerSegmentPeriodMutation = (financialModelId: string, id: string) => {
  const queryClient = useQueryClient()
  return useMutation(getUpdateCustomerSegmentPeriodMutationArgs(financialModelId, id, queryClient))
}

export const switchCustomerSegmentPeriodInput = async (financialModelId: string, body: SwitchCustomerSegmentPeriodInputRequest) => {
  const response = await axiosInstance.post<string>(
    `/financial-models/${financialModelId}/customer-segment-data/${body.customer_segment_id}/config/${body.column_name}`,
    {},
    {
      params: {
        input_type: body.input_type,
        year: body.year,
        month: body.month
      }
    }
  )
  return response.data
}

export const useSwitchCustomerSegmentPeriodInputMutation = (financialModelId: string) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: SwitchCustomerSegmentPeriodInputRequest) => switchCustomerSegmentPeriodInput(financialModelId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CUSTOMER_SEGMENTS_DATA_KEY.concat([financialModelId])
      })
      resetGlobalQueryKey([TOTALS_KEY])
    }
  })
}

export const getCustomerTotal = async (financialModelId: string, period: PeriodType, view?: PeriodTypeView) => {
  const response = await axiosInstance.get<CustomerTotalResponse>(`/financial-models/${financialModelId}/customer-segments/totals`, {
    params: {
      type: period,
      view: view ?? `monthly`
    }
  })
  return response.data
}

export const useSuspenseGetCustomerTotalQuery = (financialModelId: string, period: PeriodType, view?: PeriodTypeView) =>
  useSuspenseQuery({
    queryKey: CUSTOMER_SEGMENTS_KEY.concat(TOTALS_KEY).concat([financialModelId, period, view ?? `monthly`]),
    queryFn: () => getCustomerTotal(financialModelId, period, view)
  })
